import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ApiError } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Alert, Button, Field } from '../ui/primitives.tsx';
import { Logo, useDocumentTitle } from '../ui/Logo.tsx';

/**
 * Entrada do painel — a tela onde a marca fala mais alto (decisão 43): o
 * fundo de grade com a luz azul, o monograma com o brilho atrás, e o título
 * com a virada em serifada itálica, como nos criativos da Pure Layout. O
 * formulário fica num cartão translúcido. No celular, tudo empilha.
 */
export function Login() {
  useDocumentTitle('Entrar');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Volta para a página que o usuário tentou abrir antes do 401, em vez de
  // jogar todo mundo no dashboard.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="app-backdrop relative grid min-h-full overflow-hidden lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <section className="relative flex flex-col justify-between gap-10 px-6 py-8 sm:px-12 lg:py-12">
        {/* O monograma com a luz azul atrás — o "brilho atrás do elemento principal" da identidade. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 right-[-220px] hidden -translate-y-1/2 lg:block">
          <div className="absolute inset-[-120px] rounded-full bg-[radial-gradient(circle,rgb(10_92_255/0.35),transparent_62%)]" />
          <img src="/brand/pl-mark-192.png" alt="" className="relative w-[420px] opacity-[0.16] select-none" draggable={false} />
        </div>
        <Logo size={30} product="Prospector" />
        <div className="relative max-w-xl">
          <h1 className="text-[34px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--color-ink)] sm:text-[46px]">
            Cada abordagem começa{' '}
            <span className="font-serif font-normal tracking-normal text-[var(--color-serif-blue)] italic">
              por quem olhou o negócio antes de escrever.
            </span>
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[var(--color-ink-soft)]">
            Empresas encontradas, qualificadas e analisadas. A IA redige com o que é fato; cada mensagem passa por você antes
            de sair.
          </p>
        </div>
        <p className="relative hidden text-xs text-[var(--color-ink-faint)] lg:block">Pure Layout · Soluções em tecnologia</p>
      </section>

      <section className="relative flex items-center justify-center px-6 pb-12 sm:px-12 lg:py-12">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[rgb(11_13_20/0.72)] p-7 shadow-[var(--shadow-pop)] backdrop-blur-xl"
        >
          <h2 className="text-xl font-semibold">Entrar</h2>
          <p className="mt-1 mb-7 text-sm text-[var(--color-ink-soft)]">Use o e-mail e a senha da sua conta.</p>

          <div className="flex flex-col gap-4">
            <Field
              label="E-mail"
              id="login-email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              label="Senha"
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="mt-4">
              <Alert>{error}</Alert>
            </div>
          )}

          <Button type="submit" variant="primary" loading={submitting} className="mt-6 w-full">
            Entrar
          </Button>
        </form>
      </section>
    </main>
  );
}
